/**
 * Reports — run history, outcomes and what is newly broken.
 *
 * Ordered around the question people actually arrive with: "is something broken
 * that was not broken yesterday?" So newly-failing sits at the top, ahead of the
 * totals. A list of 30 failures where 29 have been red for a fortnight buries the
 * one that matters, and that is how a suite stops being read.
 */
import { useEffect, useMemo, useState } from 'react';
import { appOf } from './LiveResults';
import { TestDiagnostics } from './TestDiagnostics';
import { ExportModal } from './ExportModal';
import { ItemFormModal } from './TrackerScreen';
import { api, ApiError, type AppDecl, type EnvironmentName, type QaEnvironment, type QaItemDraft, type QaOptions, type RunSummary } from '../api';

/** The tracker's Environment vocabulary is broader than a run's (it also has
 *  "Demo", for manual checks outside the automated environments) — a run's
 *  environment always maps onto one of the other three. */
function qaEnvironmentFor(env: EnvironmentName): QaEnvironment {
  return env === 'dev' ? 'Dev' : env === 'qa' ? 'QA' : 'Production';
}

interface Outcome {
  title: string; file: string; status: string; durationMs: number;
  unstable?: boolean; error?: string; traceUrl?: string;
  screenshotUrls?: string[]; newlyFailing?: boolean;
  /** Recorded server-side as the run streams — see console/shared/testLines.ts. */
  suite?: string; attempts?: number; at?: string;
}

const STATUS_PILL: Record<string, string> = {
  passed: 'pill-pass', failed: 'pill-fail', error: 'pill-fail',
  stopped: 'pill-info', queued: 'pill-info', running: 'pill-accent',
};

function ago(iso?: string): string {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function duration(from?: string, to?: string): string {
  if (!from) return '—';
  const ms = (to ? Date.parse(to) : Date.now()) - Date.parse(from);
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function Reports({ onOpenRun, reporterEmail }: { onOpenRun: (id: string) => void; reporterEmail: string }) {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [environment, setEnvironment] = useState<EnvironmentName | 'all'>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [detail, setDetail] = useState<RunSummary | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [applications, setApplications] = useState<AppDecl[]>([]);
  const [exporting, setExporting] = useState(false);
  const [qaOptions, setQaOptions] = useState<QaOptions | null>(null);
  const [bugDraftFor, setBugDraftFor] = useState<Outcome | null>(null);
  const [bugNotice, setBugNotice] = useState<string | null>(null);

  useEffect(() => {
    api.matrix().then((m) => setApplications(m.applications)).catch(() => {});
    api.qaOptions().then(setQaOptions).catch(() => {});
  }, []);

  useEffect(() => {
    const load = () =>
      api.runs(environment === 'all' ? undefined : environment)
        .then((r) => setRuns(r.runs))
        .catch(() => {})
        .finally(() => setLoading(false));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [environment]);

  useEffect(() => {
    if (!selected) { setOutcomes([]); setDetail(null); return; }
    setExpanded(null);
    api.run(selected)
      .then((r) => {
        setDetail(r.run as RunSummary);
        setOutcomes(((r.run as { outcomes?: Outcome[] }).outcomes ?? []) as Outcome[]);
      })
      .catch(() => { setOutcomes([]); setDetail(null); });
  }, [selected]);

  const finished = useMemo(
    () => runs.filter((r) => ['passed', 'failed', 'stopped', 'error'].includes(r.status)),
    [runs],
  );

  const stats = useMemo(() => {
    const recent = finished.slice(0, 20);
    return {
      // Deliberately NOT a pass rate over recent history: a real bug that got found,
      // fixed and redeployed drags that number down for no reason that matters —
      // what happened is over, and the number would just be relitigating it. What
      // actually matters is the same question every time: is the most recent run
      // clean, and is anything currently unstable?
      unstable: recent.reduce((n, r) => n + (r.counts?.unstable ?? 0), 0),
      lastFailure: finished.find((r) => r.status === 'failed'),
      mostRecent: finished[0],
    };
  }, [finished]);

  async function deleteRun(id: string) {
    if (!window.confirm('Delete this run and its artifacts? This cannot be undone.')) return;
    setDeleting(id);
    setNotice(null);
    try {
      await api.deleteRun(id);
      setRuns((prev) => prev.filter((r) => r.id !== id));
      if (selected === id) setSelected(null);
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : String(e));
    } finally {
      setDeleting(null);
    }
  }

  const newlyFailing = outcomes.filter((o) => o.newlyFailing);
  const failures = outcomes.filter((o) => !o.newlyFailing && ['failed', 'timedOut'].includes(o.status));
  const unstable = outcomes.filter((o) => o.unstable);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center">
        <button
          className="btn btn-secondary ml-auto text-xs"
          onClick={() => setExporting(true)}
          data-testid="export-bugs"
        >
          Export
        </button>
      </div>

      {exporting && (
        <ExportModal applications={applications} onClose={() => setExporting(false)} />
      )}

      {/* Headline numbers. No pass rate: it drags down for bugs that were already
          found, fixed and redeployed — a stat that penalises the exact thing this
          console exists to help with. What is shown instead is what is still true
          right now: the total history kept, whatever is unstable, when things last
          broke, and whether the most recent run is clean. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total runs" value={String(runs.length)} />
        <Stat
          label="Unstable tests"
          value={String(stats.unstable)}
          tone={stats.unstable > 0 ? 'warn' : undefined}
          hint="Passed only after a retry — not green"
        />
        <Stat
          label="Last failure"
          value={stats.lastFailure ? ago(stats.lastFailure.finishedAt) : 'none'}
          tone={stats.lastFailure ? 'fail' : 'pass'}
        />
        <Stat
          label="Most recent run"
          value={stats.mostRecent ? stats.mostRecent.status : '—'}
          tone={
            !stats.mostRecent ? undefined
            : stats.mostRecent.status === 'passed' ? 'pass'
            : stats.mostRecent.status === 'failed' || stats.mostRecent.status === 'error' ? 'fail'
            : 'warn'
          }
          hint={stats.mostRecent ? `${stats.mostRecent.presetLabel} · ${ago(stats.mostRecent.finishedAt)}` : undefined}
        />
      </div>

      {notice && (
        <div className="surface px-4 py-2.5 text-sm" style={{ borderColor: 'hsl(var(--fail) / 0.4)', color: 'hsl(var(--fail))' }}>
          {notice}
        </div>
      )}

      {bugNotice && (
        <div className="surface flex items-center gap-3 px-4 py-2.5 text-sm" style={{ borderColor: 'hsl(var(--pass) / 0.4)', color: 'hsl(var(--pass))' }}>
          {bugNotice}
          <button className="btn btn-ghost ml-auto text-xs" onClick={() => setBugNotice(null)}>Dismiss</button>
        </div>
      )}

      {bugDraftFor && qaOptions && (
        <ItemFormModal
          options={qaOptions}
          reporterEmail={reporterEmail}
          lockType
          title="Create bug from failed test"
          initial={{
            title: bugDraftFor.suite ? `${bugDraftFor.suite} › ${bugDraftFor.title}` : bugDraftFor.title,
            itemType: 'bug',
            description: `Automated test failure captured from a QA Console run${detail ? ` (${detail.presetLabel} · ${detail.environment})` : ''}.`,
            stepsToReproduce: `Run the automated test "${bugDraftFor.title}"${bugDraftFor.suite ? ` in suite "${bugDraftFor.suite}"` : ''}.\nFile: ${bugDraftFor.file}`,
            expectedResult: 'The test should pass.',
            actualResult: bugDraftFor.error ? bugDraftFor.error.split('\n').slice(0, 8).join('\n') : 'The test failed — see the run diagnostics for the full error.',
            module: appOf(bugDraftFor.file),
            environment: detail ? qaEnvironmentFor(detail.environment) : undefined,
          }}
          onClose={() => setBugDraftFor(null)}
          onSubmit={async (draft: QaItemDraft) => {
            const { item } = await api.createQaItem(draft);
            setBugDraftFor(null);
            setBugNotice(`${item.displayId} — "${draft.title}" created in the QA Tracker backlog.`);
          }}
        />
      )}

      <div className="grid gap-5 lg:grid-cols-[22rem_1fr]">
        {/* History */}
        <section className="surface flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
            <h2 className="text-sm font-semibold">History</h2>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as never)}
              className="field ml-auto w-auto py-1 text-xs"
              data-testid="reports-env-filter"
            >
              <option value="all">All environments</option>
              <option value="dev">dev</option>
              <option value="qa">qa</option>
              <option value="prod">prod</option>
            </select>
          </div>

          <div className="max-h-[32rem] overflow-auto">
            {loading && <p className="p-4 text-sm" style={{ color: 'hsl(var(--text-faint))' }}>Loading…</p>}
            {!loading && runs.length === 0 && (
              <div className="p-6 text-center">
                <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>No runs yet.</p>
                <p className="mt-1 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                  Start one from the Runs tab and it will appear here.
                </p>
              </div>
            )}
            {runs.map((run) => {
              const canDelete = !['queued', 'running'].includes(run.status);
              return (
              <div
                key={run.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelected(run.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelected(run.id); }}
                data-testid={`history-${run.id}`}
                className="group flex w-full cursor-pointer flex-col gap-1.5 px-4 py-3 text-left transition hover:opacity-90"
                style={{
                  borderBottom: '1px solid hsl(var(--border))',
                  background: selected === run.id ? 'hsl(var(--bg-overlay))' : undefined,
                }}
              >
                <div className="flex items-center gap-2">
                  <span className={`pill ${STATUS_PILL[run.status] ?? 'pill-info'}`}>
                    {run.status === 'running' && <span className="dot dot-live" />}
                    {run.status}
                  </span>
                  <span className="text-sm font-medium">{run.presetLabel}</span>
                  <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{run.environment}</span>

                  {/* Only reachable for a finished run — deleting a live one would
                      pull the record out from under a container still reporting
                      to it. Hidden until hover so the list stays calm to scan. */}
                  {canDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); void deleteRun(run.id); }}
                      disabled={deleting === run.id}
                      data-testid={`delete-${run.id}`}
                      title="Delete this run"
                      className="ml-auto shrink-0 rounded px-1.5 py-0.5 text-xs opacity-0 transition group-hover:opacity-100"
                      style={{ color: 'hsl(var(--text-faint))' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'hsl(var(--fail))'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'hsl(var(--text-faint))'; }}
                    >
                      {deleting === run.id ? '…' : '✕'}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                  <span>{run.scopeLabel}</span>
                  {run.counts?.total > 0 && (
                    <span className="tabular">
                      · {run.counts.passed}/{run.counts.total}
                      {run.counts.unstable > 0 && (
                        <span style={{ color: 'hsl(var(--warn))' }}> · {run.counts.unstable} unstable</span>
                      )}
                    </span>
                  )}
                  <span className="ml-auto tabular">{duration(run.startedAt, run.finishedAt)}</span>
                  <span className="tabular">{ago(run.queuedAt)}</span>
                </div>
              </div>
              );
            })}
          </div>
        </section>

        {/* Detail */}
        <section className="surface flex flex-col overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
            <h2 className="text-sm font-semibold">
              {selected ? 'Run detail' : 'Select a run'}
            </h2>
            {selected && (
              <button onClick={() => onOpenRun(selected)} className="btn btn-ghost ml-auto text-xs">
                Open live view →
              </button>
            )}
          </div>

          {!selected ? (
            <p className="p-6 text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
              Pick a run from the history to see its tests, failures and artifacts.
            </p>
          ) : (
          <>
          {/* Everything about the run itself, so the panel answers "what was this?"
              without going back to the live view: what ran, where, who started it,
              when it began and ended, how long it took, and the totals. */}
          {detail && (
            <div className="border-b p-4" style={{ borderColor: 'hsl(var(--border))' }}>
              <dl className="grid gap-x-6 gap-y-1.5 text-xs" style={{ gridTemplateColumns: 'auto 1fr auto 1fr' }}>
                <dt style={{ color: 'hsl(var(--text-faint))' }}>What ran</dt>
                <dd className="font-medium">{detail.presetLabel}</dd>
                <dt style={{ color: 'hsl(var(--text-faint))' }}>Environment</dt>
                <dd className="font-medium">{detail.environment}</dd>

                <dt style={{ color: 'hsl(var(--text-faint))' }}>Applications</dt>
                <dd>{detail.scopeLabel}</dd>
                <dt style={{ color: 'hsl(var(--text-faint))' }}>Started by</dt>
                <dd>{detail.triggeredBy}</dd>

                <dt style={{ color: 'hsl(var(--text-faint))' }}>Started</dt>
                <dd className="tabular">
                  {detail.startedAt ? new Date(detail.startedAt).toLocaleString() : '—'}
                </dd>
                <dt style={{ color: 'hsl(var(--text-faint))' }}>Finished</dt>
                <dd className="tabular">
                  {detail.finishedAt ? new Date(detail.finishedAt).toLocaleString() : 'still running'}
                </dd>

                <dt style={{ color: 'hsl(var(--text-faint))' }}>Duration</dt>
                <dd className="tabular">{duration(detail.startedAt, detail.finishedAt)}</dd>
                <dt style={{ color: 'hsl(var(--text-faint))' }}>Result</dt>
                <dd>
                  <span className={`pill ${
                    detail.status === 'passed' ? 'pill-pass'
                    : detail.status === 'failed' || detail.status === 'error' ? 'pill-fail'
                    : 'pill-info'}`}
                  >
                    {detail.status}
                  </span>
                </dd>
              </dl>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm tabular" data-testid="run-totals">
                <span><strong>{outcomes.length}</strong> tests</span>
                <span className="text-pass">{outcomes.filter((o) => o.status === 'passed' && !o.unstable).length} passed</span>
                <span className="text-fail">{outcomes.filter((o) => o.status === 'failed' || o.status === 'timedOut').length} failed</span>
                <span className="text-warn">{outcomes.filter((o) => o.unstable).length} unstable</span>
                <span style={{ color: 'hsl(var(--text-faint))' }}>
                  {outcomes.filter((o) => o.status === 'skipped').length} skipped
                </span>
              </div>
            </div>
          )}

          {outcomes.length === 0 ? (
            <p className="p-6 text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
              No per-test detail recorded for this run. Runs started before per-test
              recording was added have none — a new run will.
            </p>
          ) : (
            <div className="max-h-[32rem] overflow-auto p-4">
              {newlyFailing.length > 0 && (
                <Group
                  title="Newly failing"
                  tone="fail"
                  hint="Failed this run, passed the previous one. Start here."
                  items={newlyFailing}
                  onCreateBug={setBugDraftFor}
                />
              )}
              {failures.length > 0 && (
                <Group
                  title="Still failing"
                  tone="fail"
                  hint="Also failed last time."
                  items={failures}
                  onCreateBug={setBugDraftFor}
                />
              )}
              {unstable.length > 0 && (
                <Group
                  title="Unstable"
                  tone="warn"
                  hint="Passed only after a retry. Playwright counts these as passes; they are not."
                  items={unstable}
                />
              )}
              {newlyFailing.length + failures.length + unstable.length === 0 && (
                <div className="py-6 text-center">
                  <p className="pill pill-pass mx-auto">All {outcomes.length} tests passed</p>
                </div>
              )}

              {/* Every test, not only the interesting ones — expandable, so the same
                  detail available in the live view is available afterwards. Grouping
                  above answers "what should I look at?"; this answers "what ran?" */}
              <div className="mt-2">
                <div className="label mb-1">All tests</div>
                <ul style={{ border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)' }}>
                  {outcomes.map((o) => {
                    const key = `${o.file}::${o.title}`;
                    const failed = o.status === 'failed' || o.status === 'timedOut';
                    return (
                      <li key={key} style={{ borderBottom: '1px solid hsl(var(--border) / 0.5)' }}>
                        <div
                          className="flex cursor-pointer items-baseline gap-2.5 px-3 py-1.5 text-sm hover:opacity-80"
                          onClick={() => setExpanded(expanded === key ? null : key)}
                          data-testid={`report-row-${key}`}
                        >
                          <span
                            className="w-4 shrink-0 text-center"
                            style={{
                              color:
                                o.unstable ? 'hsl(var(--warn))'
                                : failed ? 'hsl(var(--fail))'
                                : o.status === 'skipped' ? 'hsl(var(--text-faint))'
                                : 'hsl(var(--pass))',
                            }}
                          >
                            {o.unstable ? '!' : failed ? '✕' : o.status === 'skipped' ? '–' : '✓'}
                          </span>
                          <span className="w-24 shrink-0 truncate text-xs" style={{ color: 'hsl(var(--text-faint))' }} title={o.file}>
                            {appOf(o.file)}
                          </span>
                          <span className="flex-1 truncate" title={`${o.file} — ${o.suite ?? ''} › ${o.title}`}>
                            {o.suite && (
                              <span className="mr-2 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{o.suite}</span>
                            )}
                            {o.title}
                            {o.unstable && (
                              <span className="ml-2 text-xs text-warn">
                                unstable — passed only after {(o.attempts ?? 2) - 1} retr{(o.attempts ?? 2) - 1 === 1 ? 'y' : 'ies'}
                              </span>
                            )}
                          </span>
                          <span className="w-14 shrink-0 text-right text-xs tabular" style={{ color: 'hsl(var(--text-faint))' }}>
                            {o.durationMs ? `${(o.durationMs / 1000).toFixed(1)}s` : ''}
                          </span>
                          <span className="w-16 shrink-0 text-right text-xs tabular" style={{ color: 'hsl(var(--text-faint))' }}>
                            {o.at ? new Date(o.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                          </span>
                          <span className="w-4 shrink-0 text-center text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                            {expanded === key ? '▾' : '▸'}
                          </span>
                        </div>

                        {expanded === key && selected && (
                          <TestDiagnostics
                            runId={selected}
                            testTitle={o.title}
                            test={{
                              title: o.title,
                              suite: o.suite ?? '',
                              file: o.file,
                              app: appOf(o.file),
                              status: o.status,
                              unstable: o.unstable,
                              attempts: o.attempts,
                            }}
                            runFinished
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
          </>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, tone, hint }: { label: string; value: string; tone?: 'pass' | 'fail' | 'warn'; hint?: string }) {
  const colour = tone ? `hsl(var(--${tone}))` : 'hsl(var(--text))';
  return (
    <div className="surface p-4" title={hint}>
      <p className="label">{label}</p>
      <p className="mt-1.5 text-2xl font-semibold capitalize tabular" style={{ color: colour }}>{value}</p>
      {hint && <p className="mt-1 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{hint}</p>}
    </div>
  );
}

function Group({
  title, tone, hint, items, onCreateBug,
}: {
  title: string; tone: 'fail' | 'warn'; hint: string; items: Outcome[];
  /** Absent for the Unstable group — a flaky pass isn't a failure to file a bug against. */
  onCreateBug?: (o: Outcome) => void;
}) {
  return (
    <div className="mb-5">
      <div className="mb-2 flex items-baseline gap-2">
        <h3 className="text-sm font-semibold" style={{ color: `hsl(var(--${tone}))` }}>
          {title} ({items.length})
        </h3>
        <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{hint}</span>
      </div>
      <ul className="flex flex-col gap-2">
        {items.map((o, i) => (
          <li key={`${o.file}:${o.title}:${i}`} className="surface-raised p-3">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-sm font-medium">{o.title}</span>
              <span className="text-xs tabular" style={{ color: 'hsl(var(--text-faint))' }}>
                {(o.durationMs / 1000).toFixed(1)}s
              </span>
              <span className="ml-auto flex items-center gap-3 text-xs">
                {o.traceUrl && <a href={o.traceUrl} className="underline" data-testid="trace-link">trace</a>}
                {(o.screenshotUrls ?? []).map((u, n) => (
                  <a key={u} href={u} className="underline" data-testid="screenshot-link">
                    screenshot{(o.screenshotUrls ?? []).length > 1 ? ` ${n + 1}` : ''}
                  </a>
                ))}
                {onCreateBug && (
                  <button className="btn btn-secondary py-0.5 text-xs" onClick={() => onCreateBug(o)} data-testid="qa-create-bug">
                    Create bug
                  </button>
                )}
              </span>
            </div>
            <p className="mt-1 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{o.file}</p>
            {o.error && (
              <pre className="log mt-2 overflow-auto rounded p-2 text-xs" style={{ color: 'hsl(var(--fail))' }}>
                {o.error.split('\n').slice(0, 3).join('\n')}
              </pre>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
